import { type ResolvedIntlConfig } from 'react-intl-next';

import db_cs_CZ from '../locales/cs-CZ.json?url';
import db_da_DK from '../locales/da-DK.json?url';
import db_de_DE from '../locales/de-DE.json?url';
import db_en_GB from '../locales/en-GB.json?url';
import db_en_US from '../locales/en-US.json?url';
import db_en_ZZ from '../locales/en-ZZ.json?url';
import db_en_pseudo from '../locales/en-pseudo.json?url';
import db_en from '../locales/en.json?url';
import db_es from '../locales/es.json?url';
import db_fi_FI from '../locales/fi-FI.json?url';
import db_fr_FR from '../locales/fr-FR.json?url';
import db_hu_HU from '../locales/hu-HU.json?url';
import db_it_IT from '../locales/it-IT.json?url';
import db_ja_JP from '../locales/ja-JP.json?url';
import db_ko_KR from '../locales/ko-KR.json?url';
import db_nb_NO from '../locales/nb-NO.json?url';
import db_nl_NL from '../locales/nl-NL.json?url';
import db_pl_PL from '../locales/pl-PL.json?url';
import db_pt_BR from '../locales/pt-BR.json?url';
import db_ru_RU from '../locales/ru-RU.json?url';
import db_sv_SE from '../locales/sv-SE.json?url';
import db_tr_TR from '../locales/tr-TR.json?url';
import db_xx from '../locales/xx.json?url';
import db_zh_CN from '../locales/zh-CN.json?url';
import db_zh_TW from '../locales/zh-TW.json?url';

export const supportedLocale = [
  'en-GB',
  'en-US',
  'de-DE',
  'cs-CZ',
  'ko-KR',
  // ATM pt-PT is deprecated but no decision has been made whether to swap users over to pt-BR,
  // so we'll fall back to en-US for it.
  // Nonetheless, we support pt-BR (brasilian portuguese) natively
  'pt-BR',
  'fr-FR',
  'es-ES',
  'it-IT',
  'ja-JP',
  'nl-NL',
  'nb-NO',
  'da-DK',
  'sv-SE',
  'fi-FI',
  'ru-RU',
  'pl-PL',
  'tr-TR',
  'hu-HU',
  'zh-CN',
  'zh-TW',
  // Debug locales
  'en-pseudo',
  'xx',
  'en',
  'en-ZZ',
] as const;

export type SupportedLocale = (typeof supportedLocale)[number];

const databasesLocaleToUrlMap: Record<SupportedLocale, string> = {
  'en-GB': db_en_GB,
  'en-US': db_en_US,
  'de-DE': db_de_DE,
  'cs-CZ': db_cs_CZ,
  'ko-KR': db_ko_KR,
  'pt-BR': db_pt_BR,
  'fr-FR': db_fr_FR,
  'es-ES': db_es,
  'it-IT': db_it_IT,
  'ja-JP': db_ja_JP,
  'nl-NL': db_nl_NL,
  'nb-NO': db_nb_NO,
  'da-DK': db_da_DK,
  'sv-SE': db_sv_SE,
  'fi-FI': db_fi_FI,
  'ru-RU': db_ru_RU,
  'pl-PL': db_pl_PL,
  'tr-TR': db_tr_TR,
  'hu-HU': db_hu_HU,
  'zh-CN': db_zh_CN,
  'zh-TW': db_zh_TW,
  'en-pseudo': db_en_pseudo,
  xx: db_xx,
  en: db_en,
  'en-ZZ': db_en_ZZ,
};

const getUrlFromProductLocale = (locale: string) => {
  const localeToLoad = isLocaleSupported(locale) ? locale : 'en-US';
  return databasesLocaleToUrlMap[localeToLoad];
};

export function isLocaleSupported(locale: string): locale is SupportedLocale {
  return supportedLocale.includes(locale as SupportedLocale);
}

/**
 * Lazily loads the locale messages for a given locale.
 * @param locale The locale to load from
 * @returns The locale messages for the given locale
 */
export const fetchLocaleMessages = async (
  locale: string
): Promise<ResolvedIntlConfig['messages']> => {
  const url = getUrlFromProductLocale(locale);
  return await fetch(url).then((response) => response.json());
};

export type ExternalMessageLoader = () => Promise<{
  // Since we don't get message types, we need to declare that the .default export contains the messages
  // from the external dependency import
  default: Record<string, string>;
}>;
export type ExternalMessageLoaders = Record<
  // The key, eg "editor"
  string,
  // An array of the loader functions that will return the messages for that key
  ExternalMessageLoader[]
>;

export function getExternalMessagesPromiseFromLoaders(
  externalMessageLoaders: ExternalMessageLoaders
) {
  return Object.entries(externalMessageLoaders).map(async ([key, loadFns]) => {
    const results = await Promise.all(loadFns.map((loadFn) => loadFn()));
    const messages = results.reduce((acc, result) => ({ ...acc, ...result.default }), {});
    return { key, messages };
  });
}
